/**
 * The stock structure — companies, points of sale, depots — in the shape the
 * catalogue declares (`StockStructure`).
 *
 * One loader for the two places that need it, the context panel and the
 * consultation's filters, so both offer the same lists. Only what the back office
 * shows: a context naming a hidden company would scope every list to nothing. Hiding from
 * *customers* is another switch and changes nothing here. The server has already left out
 * a depot of a hidden company or point of sale.
 *
 * The context band outlives every screen, so it would keep the lists it read
 * at sign-in. `structureChanged` is what the structure screen calls after an
 * edit; `useStructureVersion` is what a reader puts in its fetcher's
 * dependencies to read the structure again.
 */

import { useSyncExternalStore } from "react";
import type { StockStructure } from "@hardauto/feature-catalog";
import { ReferentialApi } from "@hardauto/feature-referential";

import { client } from "./api";

export async function loadStockStructure(): Promise<StockStructure> {
  const api = new ReferentialApi(client);
  const [companies, points, sites] = await Promise.all([
    api.listCompanies(true, false),
    api.listPointsOfSale(true, false),
    api.listSites(true, false),
  ]);
  const active = new Set(companies.items.map((company) => company.code));
  return {
    companies: companies.items.map((company) => ({ code: company.code, name: company.name })),
    pointsOfSale: points.items.map((point) => ({
      code: point.code,
      name: point.name,
      city: point.city,
    })),
    sites: sites.items
      // A depot not yet attached to a company stays: it is not hidden, only
      // unfinished.
      .filter((site) => !site.company_code || active.has(site.company_code))
      .map((site) => ({
        code: site.code,
        name: site.name,
        companyCode: site.company_code,
        pointOfSaleCode: site.point_of_sale_code,
        city: site.city,
      })),
  };
}

let version = 0;
const listeners = new Set<() => void>();

/** Tell every reader of the structure to read it again. */
export function structureChanged(): void {
  version += 1;
  for (const listener of listeners) listener();
}

/** A number that changes each time the structure was edited. */
export function useStructureVersion(): number {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => version,
  );
}
